import {
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "@/components/LightTooltip";

export type IntentSignalScoreUi = "ALL" | "MEDIUM" | "HIGH";

export interface IntentThresholdSelectorProps {
  value: IntentSignalScoreUi;
  onChange: (value: IntentSignalScoreUi) => void;
  disabled?: boolean;
}

const THRESHOLD_OPTIONS = [
  {
    value: "ALL" as const,
    label: "ALL",
    tooltip: "Include all intent signals regardless of score",
  },
  {
    value: "MEDIUM" as const,
    label: "MEDIUM",
    tooltip: "Include medium and high-confidence intent signals",
  },
  {
    value: "HIGH" as const,
    label: "HIGH",
    tooltip: "Include only high-confidence intent signals",
  },
];

const INFO_TOOLTIP =
  "Controls the minimum confidence level for intent signals to be included in the export. ALL includes all signals regardless of score, HIGH includes only high-confidence signals, and MEDIUM includes medium and high-confidence signals.";

export const IntentThresholdSelector: React.FC<
  IntentThresholdSelectorProps
> = ({ value, onChange, disabled = false }) => {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="subtitle1" fontWeight="bold">
          Intent Threshold
        </Typography>
        <LightTooltip
          placement="right"
          title={
            <Typography variant="body2" color="text.secondary">
              {INFO_TOOLTIP}
            </Typography>
          }
        >
          <IconButton
            size="small"
            aria-label="Intent threshold help"
            disableRipple
            sx={{ p: 0.25 }}
          >
            <HelpOutlineIcon fontSize="inherit" sx={{ fontSize: 20 }} />
          </IconButton>
        </LightTooltip>
      </Stack>
      <RadioGroup
        value={value}
        onChange={(_e, nextValue) => onChange(nextValue as IntentSignalScoreUi)}
        row
      >
        {THRESHOLD_OPTIONS.map((option) => (
          <Tooltip key={option.value} title={option.tooltip} arrow>
            <FormControlLabel
              value={option.value}
              control={<Radio color="primary" disabled={disabled} />}
              label={option.label}
              disabled={disabled}
            />
          </Tooltip>
        ))}
      </RadioGroup>
    </Stack>
  );
};
